import CancelButton from "Common/Components/Buttons/CancelButton";
import LoadingSpinner from "Common/Components/LoaderErrorWrapper/LoadingSpinner";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { MecordColors } from "Constants/Color";
import { Fragment, useEffect, useState } from "react";
import { Col, Row, Table } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchGRNId } from "slices/Warehouse/ItemInward/GRN/thunk";

const GRNViewDetails = () => {
  const navigate = useNavigate();
  const { inventory_id } = useParams();
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch<any>();
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    if (inventory_id) {
      setLoading(true);
      dispatch(fetchGRNId(inventory_id))
        .unwrap()
        .then((data: any) => {
          setData(data?.data);
        })
        .catch((err: any) => {
          console.error("Error:", err);
          toast.error(err?.message || "Failed to fetch GRN details");
        })
        .finally(() => {
          setLoading(false);
        });
    }
    console.log("PO ID:", JSON.stringify(data?.data));
  }, [dispatch, inventory_id]);

  const displayValue = (val: any) => val || "-";

  const renderTransporterDetailsView = () => {
    if (!data?.transporter_details) return null;

    const transporterFields: any = {
      "self pickup": [
        { label: "Transporter Name", key: "transporter_name" },
        { label: "Person Number", key: "phone_no" },
      ],
      courier: [
        { label: "Docket Number", key: "docket_number" },
        { label: "Company Name", key: "company_name" },
      ],
      road: [
        { label: "Transporter Name", key: "transporter_name" },
        { label: "Vehicle Number", key: "vehicle_number" },
        { label: "Driver Name", key: "driver_name" },
        { label: "Driver Number", key: "driver_number" },
      ],
      rail: [
        { label: "Carrier Name", key: "carrier_name" },
        { label: "Train Number", key: "train_number" },
        { label: "Booking Number", key: "booking_number" },
        { label: "Origin", key: "origin" },
      ],
      air: [
        { label: "Carrier Name", key: "carrier_name" },
        { label: "Flight Number", key: "flight_number" },
        { label: "AWB Number", key: "awb_number" },
        { label: "Origin", key: "origin" },
      ],
    };

    const fields =
      transporterFields[data?.transporter_type?.toLowerCase()] || [];
    const hasDetails = fields.some(
      (field: any) => data.transporter_details[field.key],
    );

    if (!hasDetails) return null;

    return (
      <>
        {fields.map((field: any) => (
          <Col lg={3} key={field.key}>
            <div className="mb-1">
              <strong>{field.label}:</strong>
              <p>{displayValue(data.transporter_details[field.key])}</p>
            </div>
          </Col>
        ))}
      </>
    );
  };

  return (
    <Fragment>
      <PageContainer>
        <PageBreadcrumb
          title="View GRN Details"
          titleLink="/warehouse-item-inward"
          breadcrumbs={[
            { label: "Item Inward", to: "/warehouse-item-inward" },
            { label: "View GRN Details", to: "" },
          ]}
          backNavigationPath={-1}
        />
        <hr />

        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <Row className="mb-3 g-3">
              <h5 className="fw-semibold mb-1">GRN Details</h5>

              <Col lg={3}>
                <div className="mb-1">
                  <strong>GRN No.:</strong>
                  <span className="ms-1 mb-1">
                    {displayValue(data?.grn_no)}
                  </span>
                </div>
              </Col>
              <Col lg={3}>
                <div className="mb-1">
                  <strong>GRN Date:</strong>
                  <span className="ms-1 mb-1">
                    <FormattedDate date={data?.created_at} />
                  </span>
                </div>
              </Col>
            </Row>

            <hr className="my-2" />
            {/* Inward & Supplier Details */}
            <Row className="mb-3 g-3">
              {/* Inward Details */}
              <Col lg={5} md={12}>
                <h5 className="fw-semibold mb-2">Purchase Order Details</h5>
                <div
                  className="p-3 rounded border mb-2"
                  style={{ backgroundColor: MecordColors?.ActiveBackground }}
                >
                  <Row className="g-3">
                    {/* PO No */}
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>PO No.:</strong>{" "}
                        {displayValue(data?.purchase_order?.po_no)}
                      </p>
                    </Col>

                    {/* GRN No */}

                    {/* PO Date */}
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>PO Date:</strong>{" "}
                        <FormattedDate date={data?.purchase_order?.po_date} />
                      </p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Initiated By:</strong>{" "}
                        {displayValue(data?.purchase_order?.initiated_by)}
                      </p>
                    </Col>

                    {/* Received Date */}
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Received Date:</strong>{" "}
                        <FormattedDate date={data?.received_date} />
                      </p>
                    </Col>

                    {/* Received By */}
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Received By:</strong>{" "}
                        {displayValue(data?.received_by)}
                      </p>
                    </Col>

                    {/* Status */}
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Status:</strong> {data?.status}
                      </p>
                    </Col>
                  </Row>
                </div>
              </Col>

              {/* Supplier Details */}
              <Col lg={7} md={12}>
                <h5 className="fw-semibold mb-2">Supplier Details</h5>
                <div
                  className="p-3 rounded border mb-2"
                  style={{ backgroundColor: MecordColors?.ActiveBackground }}
                >
                  <Row className="g-3">
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Supplier Name:</strong>{" "}
                        {displayValue(
                          data?.supplier_details?.name ||
                            data?.supplier?.supplier_name,
                        )}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Email:</strong>{" "}
                        {displayValue(
                          data?.supplier_details?.email ||
                            data?.supplier?.email,
                        )}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Phone Number:</strong>{" "}
                        {displayValue(
                          data?.supplier_details?.phone_number ||
                            data?.supplier?.phone_number,
                        )}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Contact Person Name:</strong>{" "}
                        {displayValue(
                          data?.supplier_details?.contact_person_name,
                        )}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Contact Person Email:</strong>{" "}
                        {displayValue(
                          data?.supplier_details?.contact_person_email,
                        )}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-2">
                        <strong>Contact Person Phone:</strong>{" "}
                        {displayValue(
                          data?.supplier_details?.contact_person_phone_number,
                        )}
                      </p>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>

            <hr className="my-2" />

            {/* Transporter Details */}
            <Row className="mb-2 g-3">
              <h5 className="fw-semibold mb-1">Transporter Details</h5>

              <Col lg={3}>
                <div className="mb-1">
                  <strong>Transport Type:</strong>
                  <p className="mb-1">{displayValue(data?.transporter_type)}</p>
                </div>
              </Col>
              {/* Render transporter details based on type */}
              {renderTransporterDetailsView()}
            </Row>

            <hr className="my-2" />

            {/* LOGISTICS */}
            <Row className="mb-2 g-3">
              <h5 className="fw-semibold mb-1">Logistics Details</h5>

              <Col lg={3}>
                <div className="mb-1">
                  <strong>Delivery Challan No.:</strong>
                  <p className="mb-1">
                    {displayValue(data?.delivery_challan_no)}
                  </p>
                </div>
              </Col>
              <Col lg={3}>
                <div className="mb-1">
                  <strong>Delivery Challan Document:</strong>
                  {data?.delivery_challan_document ? (
                    <p className="mb-1">
                      <Link target="blank" to={data.delivery_challan_document}>
                        Download file
                      </Link>
                    </p>
                  ) : (
                    <p className="text-muted mb-1">No document uploaded</p>
                  )}
                </div>
              </Col>
              <Col lg={3}>
                <div className="mb-1">
                  <strong>LR No.:</strong>
                  <p className="mb-1">{displayValue(data?.lr_no)}</p>
                </div>
              </Col>
              <Col lg={3}>
                <div className="mb-1">
                  <strong>LR Document:</strong>
                  {data?.lr_document ? (
                    <p className="mb-1">
                      <Link target="blank" to={data.lr_document}>
                        Download file
                      </Link>
                    </p>
                  ) : (
                    <p className="text-muted mb-1">No document uploaded</p>
                  )}
                </div>
              </Col>
            </Row>

            <hr className="my-2" />

            {/* Invoice */}
            <Row className="mb-2 g-3">
              <h5 className="fw-semibold mb-1">Invoice Details</h5>
              <Col lg={3}>
                <div className="mb-1">
                  <strong>Invoice No.:</strong>
                  <p className="mb-1">{displayValue(data?.invoice_no)}</p>
                </div>
              </Col>
              <Col lg={3}>
                <div className="mb-1">
                  <strong>Invoice Date:</strong>
                  <p className="mb-1">
                    <FormattedDate date={data?.invoice_date} />
                  </p>
                </div>
              </Col>
              <Col lg={3}>
                <div className="mb-1">
                  <strong>Invoice Document:</strong>
                  {data?.invoice_document ? (
                    <p className="mb-1">
                      <Link target="blank" to={data.invoice_document}>
                        Download file
                      </Link>
                    </p>
                  ) : (
                    <p className="text-muted mb-1">No document uploaded</p>
                  )}
                </div>
              </Col>
            </Row>

            <hr />

            <h5 className="fw-semibold mb-2">Products</h5>
            <div className="mb-4 mt-3" style={{ overflowX: "auto" }}>
              <Table className="table align-middle">
                <thead style={{ backgroundColor: MecordColors.LightGrayColor }}>
                  <tr>
                    <th style={{ minWidth: "100px" }}>Sr.No</th>
                    <th style={{ minWidth: "150px" }}>Product Name</th>
                    <th style={{ minWidth: "150px" }}>Product Code</th>
                    <th style={{ minWidth: "150px" }}>Ordered Quantity</th>
                    <th style={{ minWidth: "150px" }}>Unit</th>
                    <th style={{ minWidth: "240px" }}>Received Quantity</th>
                    <th style={{ minWidth: "240px" }}>Good Quantity</th>
                    <th style={{ minWidth: "240px" }}>Damaged Quantity</th>
                    <th style={{ minWidth: "180px" }}>Balance Quantity</th>
                    <th
                      style={{
                        minWidth: "130px",
                        position: "sticky",
                        right: 0,
                        background: MecordColors.LightGrayColor,
                        zIndex: 2,
                      }}
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data?.purchase_order?.products
                    ?.filter((prod: any) => prod.received_quantity > 0)
                    .map((prod: any, idx: number) => (
                      <tr key={prod.inventory_product_id || idx}>
                        <td>{displayValue(prod.sr_no || idx + 1)}</td>
                        <td>{displayValue(prod.product?.product_name)}</td>
                        <td>{displayValue(prod?.product_code)}</td>
                        <td>{prod.order_quantity}</td>
                        <td>{displayValue(prod.product?.unit?.unit_name)}</td>
                        <td>{prod.received_quantity}</td>
                        <td>{prod.quantity}</td>
                        <td>{prod.damaged_quantity}</td>
                        <td>{prod.balance_quantity}</td>

                        <td
                           style={{
                        minWidth: "130px",
                        position: "sticky",
                        right: 0,
                        background: "white",
                        zIndex: 2,
                      }}
                        >
                          <button
                            className="btn btn-sm"
                            style={{
                              backgroundColor: MecordColors?.ThemeBGColor,
                              color: "#FFF",
                            }}
                            onClick={() =>
                              navigate(
                                `/grn-items-view-details/${data?.inventory_id}/${prod?.po_product_id}`,
                              )
                            }
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </div>

            <hr className="my-3" />

            {/* Notes */}
            <Row className="mb-3 g-3">
              <Col md={12}>
                <div className="card shadow-sm border">
                  <div
                    className="p-2 rounded-top-2"
                    style={{
                      backgroundColor: MecordColors?.LightGrayColor,
                      color: MecordColors?.NormalText,
                    }}
                  >
                    <h5 className="card-title mb-0 fw-semibold">
                      <i className="mdi mdi-note-text-outline me-2"></i>
                      Notes
                    </h5>
                  </div>
                  <div
                    className="card-body"
                    // style={{ backgroundColor: MecordColors?.ActiveBackground }}
                  >
                    <div className="p-2">
                      <p style={{ whiteSpace: "pre-wrap" }} className="mb-0 text-dark">
                        {displayValue(data?.note) || (
                          <span className="text-muted fst-italic">
                            No notes available
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Attachments */}
            <Row className="mb-3 g-3">
              <Col md={12}>
                <div className="card shadow-sm border">
                  <div
                    className="p-2 rounded-top-2"
                    style={{
                      backgroundColor: MecordColors?.LightGrayColor,
                      color: MecordColors?.NormalText,
                    }}
                  >
                    <h5 className="card-title mb-0 fw-semibold">
                      <i className="mdi mdi-attachment me-2"></i>
                      Attachments
                    </h5>
                  </div>
                  <div
                    className="card-body"
                    // style={{ backgroundColor: MecordColors?.ActiveBackground }}
                  >
                    {data?.attachments &&
                    Array.isArray(data.attachments) &&
                    data.attachments.length > 0 ? (
                      <div className="row g-3">
                        {data.attachments.map(
                          (attachment: string, index: number) => (
                            <div key={index} className="col-md-6 col-lg-4">
                              <div className="border rounded p-3 bg-white">
                                <div className="d-flex align-items-center">
                                  <i className="mdi mdi-file-document-outline me-2 text-primary fs-4"></i>
                                  <div className="flex-grow-1">
                                    <p className="mb-1 fw-medium text-truncate">
                                      Attachment {index + 1}
                                    </p>
                                    <small className="text-muted">
                                      Document
                                    </small>
                                  </div>
                                  <Link
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    to={attachment}
                                    className="btn btn-sm ms-2"
                                    style={{
                                      backgroundColor:
                                        MecordColors?.ThemeBGColor,
                                      color: "#FFF",
                                    }}
                                  >
                                    <i className="mdi mdi-download me-1"></i>
                                    Download
                                  </Link>
                                </div>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <i className="mdi mdi-attachment-off text-muted fs-1 mb-2"></i>
                        <p className="text-muted mb-0">
                          No attachments available
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>

            <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-4">
              <CancelButton
                onClick={() => {
                  // Set the tab to GRN (tab 2) in sessionStorage before navigating
                  sessionStorage.setItem("WarehouseItemInwardTab", "3");
                  navigate("/warehouse-item-inward");
                }}
                disabled={loading}
                showConfirmation={false}
              />
            </div>
          </>
        )}
      </PageContainer>
    </Fragment>
  );
};

export default GRNViewDetails;
